import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faBars from '@fortawesome/fontawesome-free-solid/faBars';
import { Button, Popover, OverlayTrigger, ListGroup, ListGroupItem } from 'react-bootstrap';
import ReactTable from 'react-table';
import { AlertsService, AlertStatusesService, TimeFormattingService } from '../../services';
import { BaseTable } from '../../helpers';
import i18next from "i18next";
import config from "config";

const ActionPopover = ({alert, alertDetailPath, viewTicketPath, updateStatus, onError, ...rest}) => {
  const [alertStatuses, setAlertStatuses] = useState(null);

  const statusTitle = alert.alertStatus.description;
  const isAutoResolved = alert.alertStatus.token === 'auto_resolved';

  useEffect(() => {
    AlertStatusesService.listAll().then((response) => {
      setAlertStatuses(response.alertStatuses);
    }).catch((error) => {
      onError(i18next.t('messages.could-not-load-device'));
      console.error(error);
    });
  }, [onError]);

  return (
      <Popover id="actions-popover" {...rest} >
        { alertStatuses ?
          <div className='custom-popover'>
            <div className='custom-popover-title'>{ i18next.t('pages.alerts.actions') }</div>
            <ListGroup>
              <Link to={alertDetailPath(alert.id)} className="list-group-item">{ i18next.t('pages.alerts.view-details') } ›</Link>
              {
                alert.ticket != null && <Link to={viewTicketPath(alert.ticket.id)} className="list-group-item">{ i18next.t('pages.alerts.view-tickets') } ›</Link>
              }
            </ListGroup>
            <div className='custom-popover-title'>{ isAutoResolved ? i18next.t('pages.alerts.event-status') : i18next.t('pages.alerts.event-status-change') }</div>
            <ul className="list-unstyled status-controls">
              {
                isAutoResolved ? 
                <span>{statusTitle}</span> 
                :
                alertStatuses
                  .filter((status) => status.token !== 'auto_resolved')
                  .filter((status) => status.token !== 'unacknowledged')
                  .map((status, index) => (
                    <li key={`ActionPopover_AlertStatus_${index.toString()}`}>
                      <div className="radio">
                        <input type="radio" id={`alert_status_${status.description.toLowerCase()}`} checked={statusTitle === status.description} onChange={() => updateStatus(status, alert.id)} />
                        <label htmlFor={`alert_status_${status.description.toLowerCase()}`}>{status.description}</label>
                      </div>
                    </li>
                  ))
              }
            </ul>
          </div>
          : 
          <div className="loader">{ i18next.t('loading') }</div>
        }
      </Popover>
  )
};

const NormalTimeCell = ({ timezone, dateTime }) => (
  <span>{TimeFormattingService.renderDate(dateTime, timezone)}<br/>{TimeFormattingService.renderTime(dateTime, timezone)}</span>
);

const AlertLevelCell = ({ alertLevel }) => (
  <span className={`criticality-indicator criticality-${alertLevel.token}`}>{ i18next.t(`alert-levels.${alertLevel.token}`)}</span>
);

const AreaCell = ({ tags }) => (
  tags ? <span>{(tags.filter(({ tagType }) => tagType?.token === 'area').map(({ name }) => name).join(' '))}</span> : <span/>
);

const ActionOverlay = ({ alert, alertDetailPath, viewTicketPath, updateStatus, onError }) => {
  const overlay =
      <ActionPopover
        alert={alert}
        alertDetailPath={alertDetailPath}
        viewTicketPath={viewTicketPath}
        updateStatus={updateStatus}
        onError={onError}
      />;

  return (
      <OverlayTrigger trigger="click" rootClose placement="left" overlay={overlay} >
        <Button className="btn btn-sm btn-default">
          <FontAwesomeIcon icon={faBars} />
        </Button>
      </OverlayTrigger>
  );
};

const DisplayNameCell = ({ originatingMeasure, originatingDevice, toggleEntityDetailModal }) => {
  if (originatingMeasure != null) {
    return <div className = "deviceName" onClick={() => toggleEntityDetailModal(originatingMeasure.entitySerialNumber)} title={originatingMeasure.entitySerialNumber}>{originatingMeasure.entityDisplayName}</div>;
  } else if (originatingDevice != null) {
    return <div className = "deviceName" onClick={() => toggleEntityDetailModal(originatingDevice.serialNumber)} title={originatingDevice.serialNumber}>{originatingDevice.displayName}</div>;
  } else {
    return <div></div>
  }
};

const AlertNameCell = ({ alertNotes, responses, alertConfiguration }) => (
  <div>
    { (alertNotes || (responses && responses.length > 0)) ? <img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/line_description.svg`}/> : '' } 
    {alertConfiguration.name}
  </div>
);

const SelectionHeader = ({ selectAll, toggleSelectAll }) => (
    <div className="select-page">
        <label htmlFor="select">{ i18next.t('table-headers.select-page') }</label>
        <input type="checkbox"
                className="checkbox"
                id="select"
                checked={selectAll === 1}
                ref={(input) => {
                    if (input) {
                        input.indeterminate = selectAll === 2;
                    }
                }}
                onChange={toggleSelectAll}
        />
    </div>
);

const SelectionRow = ({ checked, onChange }) => (
    <div className="checkbox-row">
        <input type="checkbox" className="checkbox" checked={checked} onChange={onChange} />
    </div>
);

const DEFAULT_SORTED = [{id: 'occurredAt', desc: true}];

const MonitorAlertListTable = ({ deviceGroupingId, parsedOptions, onShowEntityModal, onTableOptionsChanged, onError, onSuccess, alertDetailPath, viewTicketPath }) => {
    const [tableState, setTableState] = useState({ data: [], pageSize: 5, pageCount: 0, forcePage: null, loading: false, totalElements: 0 });
    const [selected, setSelected] = useState({});
    const [selectAll, setSelectAll] = useState(0);
    const [showResolveButton, setShowResolveButton] = useState(false);

    const alertsTableRef = useRef();

    const updateStatus = useCallback((newStatus, rowId) => {
        AlertsService.updateStatus(rowId, newStatus)
            .then((_) => {
                fetchData(alertsTableRef.current.state, true);
                onSuccess(i18next.t("messages.update-event-status-success"));
            })
            .catch((error) => {
                console.error(error);
                onError(i18next.t("messages.update-event-status-failure"));
            });
    }, [alertsTableRef, onSuccess, onError]);

    useEffect(() => {
        if (alertsTableRef.current != null) {
            let options = BaseTable.buildFetchDataOptions(parsedOptions, DEFAULT_SORTED);

            options.push({ filterByGroupingId: deviceGroupingId });
            options.push({ withEntity: true });

            setTableState({ ...tableState, data: [], loading: true });

            AlertsService.getAll(options)
                .then((response) => {
                    const pageCount = alertsTableRef.current ? Math.ceil(response.page.totalElements / alertsTableRef.current.state.pageSize) : null;

                    setTableState({
                        ...tableState,
                        data: response.alerts,
                        pageCount: pageCount,
                        forcePage: (alertsTableRef.current?.state?.page ?? -1) >= pageCount ? pageCount : null,
                        loading: false,
                        totalElements: response.page.totalElements,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    onError(error.message);
                });
        }
    }, [alertsTableRef, onError]);

    const fetchData = useCallback((tableData, forceRefresh) => {
        const tableOptions = BaseTable.buildURLOptions(tableData, DEFAULT_SORTED);
        if (forceRefresh === true) {
            tableOptions.push({refresh:true});
        }
        onTableOptionsChanged(tableOptions);            
    }, [tableState, onError]);

    const anySelected = (selected) => {
        return Object.keys(selected).filter((key) => selected[key]).length;
    };

    const toggleRow = useCallback((id) => {
        let newSelected = Object.assign({}, selected);
        newSelected[id] = !selected[id];

        setSelected(newSelected);
        setSelectAll(2);
        setShowResolveButton(anySelected(newSelected) ? true : false);
    }, [selected]);

    const toggleSelectAll = useCallback(() => {
        let newSelected = {};

        if (selectAll === 0) {
            tableState.data.forEach(({id}) => newSelected[id] = true);
        }

        setSelected(newSelected);
        setSelectAll(selectAll === 0 ? 1 : 0);
        setShowResolveButton(anySelected(newSelected) ? true : false);
    }, [selectAll, tableState]);

    const handleResolveSelected = useCallback(() => {
        AlertStatusesService.listAll().then((response) => {
            const bulkResolveHash = {
                alertIds: Object.keys(selected).filter(key => selected[key]),
                alertStatus: response.alertStatuses.filter((status) => status.token === 'resolved')[0]
            };

            AlertsService.bulkUpdateStatus(bulkResolveHash).then(_ => {
                onSuccess(i18next.t('messages.alerts-resolved-success'));

                setSelected({});
                setSelectAll(0);
                setShowResolveButton(false);

                fetchData(alertsTableRef.current.state, true);
            }).catch((error) => {
                console.error(error);
                onError(i18next.t('messages.alerts-resolved-failure'));
            });

        }).catch((error) => {
            console.error(error);
            onError(i18next.t("messages.alerts-resolved-failure"));
        });    
    }, [selected, alertsTableRef]);

    let dynamicOptions = BaseTable.buildDynamicOptions(parsedOptions, tableState.forcePage, DEFAULT_SORTED);

    return (
        <div className="react-table-custom">
            <ReactTable
                className="-striped -highlight"
                ref={alertsTableRef}
                manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                data={tableState.data}
                pages={tableState.pageCount} // Display the total number of pages
                loading={tableState.loading} // Display the loading overlay when we need it
                totalElements={tableState.totalElements}
                showPaginationTop
                showResolveButton={showResolveButton}
                onResolveSelected={handleResolveSelected}
                onFetchData={fetchData}
                columns={[
                            {
                                Header: (_) => <SelectionHeader selectAll={selectAll} toggleSelectAll={toggleSelectAll} />,
                                accessor: "",
                                Cell: ({ original }) => <SelectionRow checked={selected[original.id] === true} onChange={() => toggleRow(original.id)} />,
                                sortable: false,
                                width: 70,
                                minResizeWidth: 70
                            },
                            {
                                Header: i18next.t('table-headers.event-name'),
                                accessor: "alertConfiguration.name",
                                Cell: ({ original }) => <AlertNameCell 
                                                            alertNotes={original.alertNotes}
                                                            responses={original.responses}
                                                            alertConfiguration={original.alertConfiguration}
                                                        />,
                                sortable: false,
                                minResizeWidth: 50
                            },
                            {
                                Header: i18next.t('table-headers.asset'),
                                accessor: "originatingMeasure.entityDisplayName",
                                Cell: ({ original }) => <DisplayNameCell 
                                                            originatingMeasure={original.originatingMeasure} 
                                                            originatingDevice={original.originatingDevice} 
                                                            toggleEntityDetailModal={(entitySerialNumber) => onShowEntityModal(entitySerialNumber)} 
                                                        />,
                                sortable: false,
                                minResizeWidth: 50
                            },
                            {
                                Header: i18next.t('table-headers.level'),
                                id: "alertLevel.criticalityOrder",
                                accessor: ({ alertLevel }) => <AlertLevelCell alertLevel={alertLevel} />,
                                sortable: false,
                                width: 140,
                                minResizeWidth: 50
                            },
                            {
                                Header: i18next.t('table-headers.status'),
                                id: "alertStatus",
                                accessor: ({ alertStatus }) => i18next.t(`alert-statuses.${alertStatus.token}`),
                                sortable: false,
                                width: 170,
                                minResizeWidth: 50
                            },
                            {
                                Header: i18next.t('table-headers.time'),
                                accessor: "occurredAt",
                                Cell: ({original, value}) => <NormalTimeCell timezone={original.timezone} dateTime={value} />,
                                sortable: true,
                                width: 140,
                                minResizeWidth: 50
                            },
                            {
                                Header: i18next.t('table-headers.area'),
                                accessor: "originatingMeasure.tags",
                                Cell: ({ value }) => <AreaCell tags={value} />,
                                sortable: false,
                                width: 100,
                                minResizeWidth: 50
                            },
                            {
                                Cell: ({ original }) => <ActionOverlay
                                                            alert={original}
                                                            alertDetailPath={alertDetailPath}
                                                            viewTicketPath={viewTicketPath}
                                                            updateStatus={updateStatus}
                                                            onError={onError}
                                                        />,
                                sortable: false,
                                width: 60,
                                minResizeWidth: 50
                            }
                        ]}
                {...dynamicOptions}
            />
        </div>
    );
};

export default MonitorAlertListTable;